import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  useMediaQuery,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EmailIcon from "@mui/icons-material/Email";
import ShareIcon from "@mui/icons-material/Share";
import FacebookIcon from "@mui/icons-material/Facebook";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useTranslation } from "../../context/TranslationContext";

function toHashtag(str) {
  if (!str) return "";
  return "#" + str.replace(/\s+/g, "").replace(/[^a-zA-Z0-9]/g, "");
}

function ShareStationLinks({ stationName }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const { translate } = useTranslation();
  const url = window.location.href;
  const message = stationName
    ? `${translate(
        "Community weather stations in Nunavut are reporting hourly on Silanga."
      )} ${translate("Check out the one at")} ${translate(
        stationName
      )} ${translate("for example:")} ${url}`
    : `${translate(
        "Community weather stations in Nunavut are reporting hourly on Silanga."
      )} ${translate("Click here")}: ${url}`;
  const hashtag = `#WeatherStation #Silanga${
    stationName ? " " + toHashtag(stationName) : ""
  }`;
  const isMobile = useMediaQuery("(max-width:600px)");

  const handleCopy = () => {
    navigator.clipboard.writeText(url);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Email share
  const emailSubject = `${translate("Weather at")} ${
    stationName ? translate(stationName) : translate("Silanga")
  } ${translate("on Silanga")}`;
  const emailBody = message;

  // Helper for external share links
  const openShareLink = (href) => {
    window.open(href, "_blank", "noopener,noreferrer");
  };

  const shareButtonSx = {
    justifyContent: "flex-start",
    textTransform: "none",
    alignItems: "center",
    py: isMobile ? 1.5 : 1.2,
    fontSize: isMobile ? "0.87rem" : "1rem",
    minHeight: isMobile ? 38 : 44,
    gap: 1.2,
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        width: "100%",
        mt: 2,
      }}
    >
      <Button
        variant="outlined"
        startIcon={<ShareIcon />}
        onClick={() => setModalOpen(true)}
        sx={{
          borderRadius: 2,
          px: 2.5,
          py: 1,
          fontWeight: 500,
          fontSize: { xs: "0.87rem", sm: "1rem" },
          textTransform: "none",
          boxShadow: "none",
          borderColor: "primary.main",
          color: "primary.main",
          "&:hover": {
            borderColor: "primary.dark",
          },
          mr: { xs: 2, sm: 2 },
        }}
      >
        {translate("Share")}
      </Button>
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 0,
            minWidth: isMobile ? 0 : 370,
            maxWidth: isMobile ? "98vw" : undefined,
            background: "#fff",
            boxShadow: 3,
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 500,
            pb: 0,
            pt: isMobile ? 2 : 3,
            fontSize: isMobile ? "1rem" : "1.15rem",
            letterSpacing: 0.2,
          }}
        >
          {stationName ? (
            <>
              {translate("Share")}{" "}
              <Box component="span" sx={{ display: "inline" }}>
                {translate(stationName)}
              </Box>{" "}
              {translate("Station Page")}
            </>
          ) : (
            <>
              {translate("Share")} {translate("Station Page")}
            </>
          )}
        </DialogTitle>
        <DialogContent
          sx={{
            pt: isMobile ? 1 : 2,
            pb: isMobile ? 0.5 : 1,
            px: { xs: 2, sm: 3 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: isMobile ? 1 : 1.5,
              mt: 1,
            }}
          >
            <Button
              startIcon={
                <FacebookIcon sx={{ color: "blue" }} fontSize="small" />
              }
              fullWidth
              variant="outlined"
              sx={shareButtonSx}
              onClick={() =>
                openShareLink(
                  `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                    url
                  )}&quote=${encodeURIComponent(
                    message
                  )}&hashtag=${encodeURIComponent(hashtag)}`
                )
              }
            >
              {translate("Share on facebook")}
            </Button>
            <Button
              startIcon={
                <WhatsAppIcon sx={{ color: "green" }} fontSize="small" />
              }
              fullWidth
              variant="outlined"
              sx={shareButtonSx}
              onClick={() =>
                openShareLink(
                  `https://wa.me/?text=${encodeURIComponent(message)}`
                )
              }
            >
              {translate("Share on Whatsapp")}
            </Button>
            <Button
              startIcon={<EmailIcon fontSize="small" />}
              fullWidth
              variant="outlined"
              component="a"
              href={`mailto:?subject=${encodeURIComponent(
                emailSubject
              )}&body=${encodeURIComponent(emailBody)}`}
              sx={shareButtonSx}
            >
              {translate("Share on Email")}
            </Button>
            <Button
              startIcon={<ContentCopyIcon fontSize="small" />}
              fullWidth
              variant="outlined"
              sx={shareButtonSx}
              onClick={() => {
                handleCopy();
                setModalOpen(false);
              }}
            >
              {translate("Copy Link")}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{ justifyContent: "center", pb: isMobile ? 1 : 2, pt: 0 }}
        >
          <Button
            onClick={() => setModalOpen(false)}
            variant="text"
            color="primary"
          >
            {translate("Close")}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        message={translate("Link copied!")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}

export default ShareStationLinks;
